/**
 * 图片URL生成工具
 * 封装图片代理API的使用
 */

import { apiClient } from '../adapters';

/**
 * 图片类型
 */
export type ImageType = 'cover' | 'icon' | 'banner' | 'screenshot';

/**
 * 图片尺寸配置
 */
export interface ImageSize {
  /** 宽度 */
  w?: number;
  /** 高度 */
  h?: number;
  /** 格式 */
  format?: 'webp' | 'jpg' | 'png';
  /** 质量 */
  quality?: number;
}

/**
 * 预设图片尺寸
 */
export const IMAGE_SIZES = {
  /** 移动端封面 */
  MOBILE_COVER: { w: 400, format: 'webp' as const },
  /** PC端封面 */
  DESKTOP_COVER: { w: 800, format: 'webp' as const },
  /** 轮播图大图 */
  CAROUSEL: { w: 1200, format: 'webp' as const },
  /** 游戏图标 */
  ICON: { w: 200, format: 'webp' as const },
  /** 历史记录小图标 */
  HISTORY_ICON: { w: 100, format: 'webp' as const },
  /** 截图 */
  SCREENSHOT: { w: 800, format: 'webp' as const },
} as const;

/**
 * 生成图片URL
 * @param gameId 游戏ID
 * @param type 图片类型
 * @param options 尺寸选项
 * @returns 完整的图片URL
 */
export function getImageUrl(
  gameId: string,
  type: ImageType,
  options: ImageSize = {}
): string {
  if (type === 'screenshot') {
    return apiClient.buildImageUrl(gameId, type, 0, options);
  }
  return apiClient.buildImageUrl(gameId, type as 'cover' | 'icon' | 'banner', options);
}

/**
 * 生成封面图URL（便捷方法）
 */
export function getCoverUrl(gameId: string, isMobile: boolean = false): string {
  const size = isMobile ? IMAGE_SIZES.MOBILE_COVER : IMAGE_SIZES.DESKTOP_COVER;
  return getImageUrl(gameId, 'cover', size);
}

/**
 * 生成图标URL（便捷方法）
 */
export function getIconUrl(gameId: string, small: boolean = false): string {
  const size = small ? IMAGE_SIZES.HISTORY_ICON : IMAGE_SIZES.ICON;
  return getImageUrl(gameId, 'icon', size);
}

/**
 * 生成轮播图URL（便捷方法）
 */
export function getCarouselUrl(gameId: string): string {
  return getImageUrl(gameId, 'cover', IMAGE_SIZES.CAROUSEL);
}

/**
 * 生成截图URL
 */
export function getScreenshotUrl(
  gameId: string,
  index: number,
  options: ImageSize = IMAGE_SIZES.SCREENSHOT
): string {
  return apiClient.buildImageUrl(gameId, 'screenshot', index, options);
}

/**
 * 图片加载错误处理
 * 返回默认 fallback 图片URL
 */
export function getFallbackImage(type: ImageType = 'cover'): string {
  const fallbacks: Record<ImageType, string> = {
    cover: '/images/fallback-cover.jpg',
    icon: '/images/fallback-icon.jpg',
    banner: '/images/fallback-banner.jpg',
    screenshot: '/images/fallback-screenshot.jpg',
  };
  return fallbacks[type];
}

/**
 * 构建图片URL（兼容层）
 * 如果传入的是完整URL则直接返回，否则添加代理前缀
 */
export function buildImageUrl(url: string): string {
  if (!url) return getFallbackImage('cover');
  if (url.startsWith('http')) return url;
  return url;
}
